// Pick the QUOTATION a new confirmed order is made from (Confirmed Orders > Upload).
//
// This replaced a native <select> that listed up to 200 quotations of every status. Two things were wrong
// with it. It offered quotations that are already confirmed, which the server then refuses (one confirmed
// order per RFQ), so it now asks the server for `unconfirmed` ones only. And a native select draws its
// option list in the browser's own popup, which on a small screen covered the dialog's Cancel and Upload
// buttons and could not be styled or limited, so this draws its list in the page, in flow, with a height cap.
//
// The same shape as `ConfirmedOrderSearchSelect`: opens on focus with the most recent so the list is
// browsable before you know what to type, search is the SERVER's (quote number, client, vessel, subject),
// a last-one-wins guard so a slow answer cannot repopulate a list you have moved past, keyboard and
// outside-click. Unlike it, the list is NOT absolutely positioned: inside a dialog that clips its
// children, a floating list is cut off at the dialog's edge. In flow, the dialog's own body scrolls.
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { fetchQuotationList, type QuotationListItemDto } from '../lib/quotationApi';
import { useLatestRequest } from '../hooks/useLatestRequest';
import { clientDisplayName } from '../utils/orderIdentity';
import { quotationVesselName } from '../utils/quotationVessel';

const RESULT_LIMIT = 20;

interface QuotationSearchSelectProps {
  /** The quotation picked, or null when none (or after "Change"). */
  value: QuotationListItemDto | null;
  onChange: (quotation: QuotationListItemDto | null) => void;
  disabled?: boolean;
  autoFocus?: boolean;
}

/**
 * How a quotation reads: number and vessel on the first line, the client on the second. The vessel is the
 * one rule (`quotationVesselName`: the quotation's own copy, else the RFQ's, D104.12) and "--" when
 * neither names one, so a row never silently loses its vessel. `full` is the same in one line, for the
 * accessible name.
 */
function quotationRow(q: QuotationListItemDto) {
  const vessel = quotationVesselName(q) || '--';
  const client = clientDisplayName(q.customer?.customerName) || '--';
  return { first: `${q.quotationNumber} • ${vessel}`, client, full: `${q.quotationNumber} • ${client} • ${vessel}` };
}

export const QuotationSearchSelect: React.FC<QuotationSearchSelectProps> = ({
  value,
  onChange,
  disabled = false,
  autoFocus = false,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<QuotationListItemDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const listboxId = useId();
  const beginRequest = useLatestRequest();

  // An empty query is a real search: the most recent quotations that can still be confirmed.
  useEffect(() => {
    if (!open || value) return;
    let cancelled = false;
    const isCurrent = beginRequest();
    setLoading(true);
    const t = window.setTimeout(async () => {
      try {
        const res = await fetchQuotationList({
          page: 1,
          limit: RESULT_LIMIT,
          search: query.trim(),
          unconfirmed: true,
          sortBy: 'createdAt',
          sortOrder: 'desc',
        });
        if (cancelled || !isCurrent()) return;
        setResults(res.data.quotations || []);
        setTotal(res.data.pagination?.total ?? (res.data.quotations || []).length);
        setFailed(false);
        setHighlight(0);
      } catch {
        // No server prose on screen (invariant 6); "could not load" reads differently from "none".
        if (!cancelled && isCurrent()) {
          setResults([]);
          setTotal(0);
          setFailed(true);
        }
      } finally {
        if (!cancelled && isCurrent()) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [query, open, value, beginRequest]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const choose = useCallback(
    (q: QuotationListItemDto) => {
      onChange(q);
      setQuery('');
      setOpen(false);
    },
    [onChange]
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      // Close the list only; the dialog around it must not also close on this Escape.
      if (open) e.stopPropagation();
      setOpen(false);
      return;
    }
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const q = results[highlight];
      if (q) choose(q);
    }
  };

  if (value) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
        <div className="min-w-0 text-xs">
          <div className="truncate font-semibold text-slate-800">{quotationRow(value).first}</div>
          <div className="truncate text-slate-500">{quotationRow(value).client}</div>
        </div>
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            onChange(null);
            setOpen(true);
          }}
          className="shrink-0 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div ref={boxRef}>
      <input
        value={query}
        disabled={disabled}
        autoFocus={autoFocus}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Search by quote number, client or vessel…"
        role="combobox"
        aria-label="Select quotation"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls={listboxId}
        className="h-9 w-full rounded-md border border-slate-200 px-3 text-xs outline-none focus:border-primary-500 disabled:bg-slate-50"
      />

      {open && (
        <div
          id={listboxId}
          role="listbox"
          className="mt-1 max-h-[40vh] overflow-y-auto rounded-md border border-slate-200 bg-white"
        >
          {loading && <div className="px-3 py-3 text-xs text-slate-500">Searching…</div>}

          {!loading && failed && (
            <div className="px-3 py-3 text-xs text-slate-500">
              Quotations could not be loaded. You may not have access to that screen. Ask an administrator, or try again.
            </div>
          )}

          {!loading && !failed && results.length === 0 && (
            <div className="px-3 py-3 text-xs text-slate-500">
              {query.trim()
                ? `No quotation waiting to be confirmed matches “${query.trim()}”.`
                : 'No quotation is waiting to be confirmed.'}
            </div>
          )}

          {!loading &&
            !failed &&
            results.map((q, i) => (
              <button
                type="button"
                role="option"
                aria-label={quotationRow(q).full}
                aria-selected={i === highlight}
                key={q._id}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => choose(q)}
                className={`block w-full border-b border-slate-100 px-3 py-2 text-left text-xs last:border-b-0 ${
                  i === highlight ? 'bg-slate-50' : 'hover:bg-slate-50'
                }`}
              >
                <div className="truncate font-semibold text-slate-800">{quotationRow(q).first}</div>
                <div className="truncate text-slate-500">{quotationRow(q).client}</div>
              </button>
            ))}

          {!loading && !failed && total > results.length && (
            <div className="border-t border-slate-100 px-3 py-2 text-xs text-slate-400">
              Showing the newest {results.length} of {total}. Type to narrow the list.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuotationSearchSelect;
